import React, {useCallback, useMemo, useState} from 'react';
import ModalComponent from '../ModalComponent';
import { searchConfigsFields, searchConfigsGroupingFields, selectedFields } from '../../ducks/index';
import {Button} from 'react-bootstrap';
import {duckify, filterDuck} from "../../utils/duckify";
import {compose} from "redux";
import classnames from "classnames";
import debounce from 'lodash/debounce';
import {default as Select, defaultFilterOptions} from "react-select";
import './styles/ChangeColumnsEditor.scss';
import rowHeadings from '../../../../images/admin_portal/search/search-row-headings.png';
import colHeadings from '../../../../images/admin_portal/search/search-column-headings.png';
import graphSample from '../../../../images/admin_portal/search/graph-sample.png';
import Placeholder from 'components/Placeholder';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import LinkButton from "../LinkButton/LinkButton";


const FIELDS_LIMIT = 150;

const ResultTypeOption = ({typeCss, typeValue, typeDisplayName, currentType, setType}) => {
  const onClick = () => {
    setType(typeValue);
  }

  return (
    <div className='results-display-style-box'>
      <button
        className={classnames('results-display-style-image', typeCss, currentType === typeValue ? 'active' : '')}
        onClick={onClick}
        name={typeCss}
      />
      <div>
        {typeDisplayName}
      </div>
    </div>
  );
}

const updateWhenFieldSelected = (field, groupedSelectedFields, selectedEntity, setGroupedSelectedFields) => {
  const entity = groupedSelectedFields.find((f)  => f.id === selectedEntity)
  // ensure we can't double-add
  if (!entity.options.find((option) => option.value === field.value)){
    entity.options.push(field)
    const entityIndex = groupedSelectedFields.map(e => e.id)
      .indexOf(entity.id);
    groupedSelectedFields[entityIndex] = entity
    setGroupedSelectedFields([...groupedSelectedFields])
  }
}


const removeField  = (field, groupedSelectedFields, entity, setGroupedSelectedFields) => {
  entity.options = entity.options.filter((f) =>  f.value !== field.value)
  const entityIndex = groupedSelectedFields.map(e => e.id).indexOf(entity.id);
  groupedSelectedFields[entityIndex] = entity
  setGroupedSelectedFields([...groupedSelectedFields])
}

const FieldsSelectedForEntity = ({entity, overallFields, setGroupedSelectedFields, groupedSelectedFields}) => {
  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  if (entity.options.length === 0) {
    return <React.Fragment />;
  }
  return (
    <DndContext
      onDragEnd={(event) => handleDragEnd(event, groupedSelectedFields, entity, setGroupedSelectedFields)}
      sensors={sensors}
      collisionDetection={closestCenter}>
      <SortableContext items={entity.options.map(option => option.value)} strategy={verticalListSortingStrategy}>
        <>
          <h4 className='space-above'>{entity.label}</h4>
          <div className='selected-fields-group'>
            {entity.options.map(option => (
              <SortableField entity={entity} groupedSelectedFields={groupedSelectedFields} field={option} setGroupedSelectedFields={setGroupedSelectedFields} key={option.value} />
            ))}
          </div>
        </>
      </SortableContext>
    </DndContext>
  );
}

const handleDragEnd = (event, groupedSelectedFields, entity, setGroupedSelectedFields) => {
  const options = entity.options;
  const newIndex = options.map(e => e.value).indexOf(event.over.id);
  const oldIndex = options.map(e => e.value).indexOf(event.active.id);
  entity.options = arrayMove(options, oldIndex, newIndex);
  const entityIndex = groupedSelectedFields.map(e => e.id).indexOf(entity.id);
  groupedSelectedFields[entityIndex] = entity
  setGroupedSelectedFields([...groupedSelectedFields])
}

const SortableField = ({entity, setGroupedSelectedFields, field, groupedSelectedFields})  => {

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({id: field.value,  transition: null});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };


  return ( <div {...attributes} {...listeners} style={style} ref={setNodeRef} className='selected-fields-value'>
    <span className='selected-fields-label'>{field.label}</span>
    <a className='selected-fields-x' onPointerDown={e => e.preventDefault()} onClick={() => removeField(field, groupedSelectedFields, entity, setGroupedSelectedFields)}>{'\xD7'}</a>
  </div>)
}

const DisplayTypeSelector = ({displayType, setType}) => {
  return (
    <>
      <h4>Display results in a...</h4>
      <div className='results-display-styles'>
        <ResultTypeOption typeCss='results-list' typeValue='' typeDisplayName='List' currentType={displayType} setType={setType}/>
        <ResultTypeOption typeCss='results-list-grouping' typeValue='GroupingReport' typeDisplayName='List with Groupings' currentType={displayType} setType={setType}/>
        <ResultTypeOption typeCss='results-summary' typeValue='SummaryReport' typeDisplayName='Summary Report' currentType={displayType} setType={setType}/>
        <ResultTypeOption typeCss='results-graph' typeValue='GraphingReport' typeDisplayName='Graph' currentType={displayType} setType={setType}/>
      </div>
    </>
)
  ;
}

const getFilterOptions = (options, searchString) => {
  return defaultFilterOptions(options, searchString, null,{
    ignoreAccents: true,
    ignoreCase: true,
    labelKey: 'label',
    matchPos: 'any',
    matchProp: 'any',
    trimFilter: true,
    valueKey: 'value'
  })
}

const GroupBySelector = ({rowGroupingFields, setRowGroupingFields, groupingOptions}) => (
  <div className='grouping-options space-below'>
    <h4 className='space-above'>Group by</h4>
    <Select
      className='column-editor-graph-row-group-by col-12'
      clearable={false}
      value={rowGroupingFields}
      onChange={(e) => e && setRowGroupingFields(e.value)}
      name={"column_selected_graph-row_group-by"}
      options={groupingOptions}
    />
  </div>
);

const SelectedFieldsArea = ({overallFields, groupedSelectedFields, setGroupedSelectedFields}) => (
  <>
    <h2>&nbsp;</h2>
    <h4>Selected Fields</h4>
    <span className={'helper-text'}>Drag to re-order within entities</span>
    {groupedSelectedFields && groupedSelectedFields.map((entity) => <FieldsSelectedForEntity
      groupedSelectedFields={groupedSelectedFields} entity={entity} overallFields={overallFields}
      setGroupedSelectedFields={setGroupedSelectedFields} key={entity.id}/>)}
  </>
);

const GroupSelector = ({fieldsInGroup, selectedGroupInfo, setSelectedGroupInfo, realGroup, realSubgroup, groupOptions}) => {
  // Special display when in checklist/calendar
  const inSubgroup = fieldsInGroup[0] && fieldsInGroup[0].subgroup && !selectedGroupInfo.forceOutside;
  const subgroupOptions = inSubgroup ? [...new Set(fieldsInGroup.map(option => option.subgroup))] : [];

  return (<>
    <h4 className='space-above'>
      {inSubgroup ? (<>
        <LinkButton
          onClick={() => setSelectedGroupInfo({group: selectedGroupInfo.group, subgroup: selectedGroupInfo.subgroup, forceOutside: true})}
          content='Grouping'
        />
        {` > ${realGroup}`}
      </>) : 'Grouping'}
    </h4>
    <div className='list-of-groups'>
      {groupOptions.length === 0 && <div key='placeholder' className='individual-cell'>No Groups</div>}
      {inSubgroup ?
        subgroupOptions.map(subgroup => (
          <div
            className={classnames('group-button', 'individual-cell', subgroup === realSubgroup ? 'group-selected' : '')}
            onClick={() => setSelectedGroupInfo({group: realGroup, subgroup: subgroup, forceOutside: false})}
            key={subgroup}
          >
            {subgroup}
          </div>
        )) : groupOptions.map(group => (
          <div
            className={classnames('group-button', 'individual-cell', group === realGroup ? 'group-selected' : '')}
            onClick={() => setSelectedGroupInfo({group: group, subgroup: '', forceOutside: false})}
            key={group}
          >
            {group}
          </div>
        ))}
    </div>
  </>)
};

const FieldAndCheckbox = ({className, onChange, checked, label}) => (
  <div className={classnames('individual-cell', 'custom-option-flex', className)} onClick={onChange}>
    <span className='custom-option-check-column'>
      <input
        className='contact-list-header-checkbox bulk-toggle'
        type='checkbox'
        checked={checked}
        onChange={onChange}
      />
    </span>
    <span className='custom-option-label-column'>
      {label}
    </span>
  </div>
)

const FieldSelector = ({fieldsInGroup, realSubgroup, groupedSelectedFields, setGroupedSelectedFields, selectedEntity}) => {
  const fieldsInSubgroup = fieldsInGroup.filter(option => option.subgroup === realSubgroup);

  // Which of the currently visible fields is selected?
  const selectedFieldGroup = groupedSelectedFields.find(o => o.id === selectedEntity)
  const fieldOptionValues = selectedFieldGroup.options.map((fieldOption) => fieldOption.value);
  const unselected = fieldsInSubgroup.filter(option => !fieldOptionValues.includes(option.value));

  const fieldSelected = (field) => {
    fieldOptionValues.includes(field.value) ? removeField(field, groupedSelectedFields, selectedFieldGroup, setGroupedSelectedFields) :
      updateWhenFieldSelected(field, groupedSelectedFields, selectedEntity, setGroupedSelectedFields)
  }

  const selectAllFields = () => {
    if (unselected.length > 0) {
      unselected.forEach(option => updateWhenFieldSelected(option, groupedSelectedFields, selectedEntity, setGroupedSelectedFields));
    } else {
      fieldsInSubgroup.forEach(option => removeField(option, groupedSelectedFields, selectedFieldGroup, setGroupedSelectedFields));
    }
  }

  return (<>
    <h4 className='space-above'>Fields</h4>
    <div className='list-of-fields'>
      {fieldsInSubgroup.length > 0 && (
        <>
          <FieldAndCheckbox
            label='Select All'
            className='select-all-section'
            onChange={selectAllFields}
            checked={unselected.length === 0}
            key='select-all'
          />
          {fieldsInSubgroup.map(option => (
            <FieldAndCheckbox
              label={option.label}
              onChange={() => fieldSelected(option)}
              checked={fieldOptionValues.includes(option.value)}
              key={option.value}
            />
          ))}
        </>)}
      {fieldsInSubgroup.length === 0 && <div key='no-fields' className='individual-cell'>No Fields</div>}
    </div>
  </>);
}

const GroupsAndFields = ({ filteredOptions, groupOptions, selectedEntity, groupedSelectedFields, setGroupedSelectedFields }) => {
  const [selectedGroupInfo, setSelectedGroupInfo] = useState({group: undefined, subgroup: '', forceOutside: false});

  // Filter down options by selected group and subgroup or fall back to filtering by the first option for both
  const realGroup = (selectedGroupInfo.group && filteredOptions.find(option => option.group === selectedGroupInfo.group)) ? selectedGroupInfo.group : (filteredOptions[0] && filteredOptions[0].group);
  const fieldsInGroup = filteredOptions.filter(option => option.group === realGroup);
  const realSubgroup = (selectedGroupInfo.subgroup && filteredOptions.find(option => option.subgroup === selectedGroupInfo.subgroup)) ? selectedGroupInfo.subgroup : (fieldsInGroup[0] && fieldsInGroup[0].subgroup);

  return (<div className='results-section'>
    <div className='group-section'>
      <GroupSelector
        selectedGroupInfo={selectedGroupInfo} setSelectedGroupInfo={setSelectedGroupInfo}
        realGroup={realGroup} realSubgroup={realSubgroup}
        fieldsInGroup={fieldsInGroup} groupOptions={groupOptions}
      />
    </div>
    <div className='field-section'>
      <FieldSelector
        fieldsInGroup={fieldsInGroup} realSubgroup={realSubgroup} selectedEntity={selectedEntity}
        groupedSelectedFields={groupedSelectedFields} setGroupedSelectedFields={setGroupedSelectedFields}
      />
    </div>
  </div>);
}

const DelayedSearchField = ({searchString, setSearchString}) => {
  const [tempString, setTempString] = useState(searchString);
  const invariant = [];

  const debounceUpdate = useCallback(
    debounce(
      (text) => setSearchString(text),
      500
    ),
    invariant
  );

  const onChange = (text) => {
    setTempString(text);
    debounceUpdate(text);
  }

  return (<input
    className='base-setup-field-input smaller-text-field'
    name='search-string'
    type='text'
    value={tempString}
    onChange={(event) => onChange(event.target.value)}
    placeholder='Search for field names'
  />);
}

const SearchFieldsArea = ({overallFields, groupedSelectedFields, setGroupedSelectedFields}) => {
  const [selectedEntity, setSelectedEntity] = useState('contact');
  const [searchString, setSearchString] = useState('');

  // Filter overall list of fields and groups
  const entityOptions = overallFields.map(entity => ({label: entity.label, value: entity.id}));
  const entityGroup = overallFields.find((e) => e.id === selectedEntity);
  const filteredOptions = useMemo(() => entityGroup ? getFilterOptions(entityGroup.options, searchString) : [], [selectedEntity, searchString]);
  const groupOptions = [...new Set(filteredOptions.map(option => option.group))];

  return (
    <>
      <h2>Fields to display</h2>
      <h4>Search</h4>
      <DelayedSearchField searchString={searchString} setSearchString={setSearchString} />
      <h4 className='space-above'>Entity</h4>
      <Select
        className='column-editor-entity'
        clearable={false}
        value={selectedEntity}
        onChange={(e) => e && setSelectedEntity(e.value)}
        name={"selected_entity"}
        options={entityOptions}
      />
      <GroupsAndFields filteredOptions={filteredOptions} groupOptions={groupOptions} selectedEntity={selectedEntity} groupedSelectedFields={groupedSelectedFields} setGroupedSelectedFields={setGroupedSelectedFields} />
    </>
  )
}

const SearchWithColumns = ({
                             displayType,
                             groupingOptions,
                             overallFields,
                             rowGroupingFields,
                             setRowGroupingFields,
                             groupedSelectedFields,
                             setGroupedSelectedFields
                           }) => (
  <div className='list-options'>
    {displayType === 'GroupingReport' &&
      <GroupBySelector
        rowGroupingFields={rowGroupingFields}
        setRowGroupingFields={setRowGroupingFields}
        groupingOptions={groupingOptions}
      />}

    <div className='col-selector-field-panel space-above'>
      <div className='two-thirds-column'>
        <div className='field-selector-sticky controls'>
          <SearchFieldsArea
            overallFields={overallFields}
            groupedSelectedFields={groupedSelectedFields}
            setGroupedSelectedFields={setGroupedSelectedFields}
          />
        </div>
      </div>
      <div className='selected-columns-fill'>
        <SelectedFieldsArea
          groupedSelectedFields={groupedSelectedFields}
          setGroupedSelectedFields={setGroupedSelectedFields}
          overallFields={overallFields}
        />
      </div>
    </div>
  </div>
);

const SummaryReportSearch = ({ groupingOptions, rowGroupingFields, setRowGroupingFields, columnGroupingFields, setColumnGroupingFields}) => {
  return (<div className='summary-options col-12'>
    <h4 className='space-above'>
      <img src={rowHeadings} />
      &nbsp;Row Headings
      <span className='required'>*</span>
    </h4>
    <Select
      className='column-editor-row-grouping'
      clearable={false}
      value={rowGroupingFields}
      onChange={(e) => e && setRowGroupingFields(e.value)}
      name={"column_selected_row_grouping"}
      options={groupingOptions}
    />
    <h4 className='space-above'>
      <img src={colHeadings}/>
      &nbsp;Column Headings
    </h4>
    <Select
      className='column-editor-col-grouping'
      clearable={false}
      value={columnGroupingFields}
      onChange={(e) => e && setColumnGroupingFields(e.value)}
      name={"column_selected_column_grouping"}
      options={groupingOptions}
    />
    <span className='required clear'>*</span>
    indicates required fields
  </div>);
};

const GraphReportSearch = ({rowGroupingFields, setRowGroupingFields, groupingOptions}) => {
  return (<div className='graph-options' >
    <Select
      className='column-editor-graph-row-grouping'
      clearable={false}
      value={rowGroupingFields}
      onChange={(e) => e && setRowGroupingFields(e.value)}
      name={"column_selected_graph_row_grouping"}
      options={groupingOptions}
    />
    <img className='graph-sample' src={graphSample} />
  </div>);
};

const LoadedColumnsEditor = ({searchConfigsFields, selectedFields, searchType, rowGroupingField, columnGroupingField, groupingFields}) => {
  const [displayType, setType] = useState(searchType);
  const [groupedSelectedFields, setGroupedSelectedFields] = useState(selectedFields);
  const [overallFields, setOverallFields] = useState(searchConfigsFields.map(x => x));
  const [columnGroupingFields, setColumnGroupingFields] = useState(columnGroupingField);
  const [rowGroupingFields, setRowGroupingFields] = useState(rowGroupingField);
  const [errors, setErrors] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const groupingOptions = [{label: '', value: ''}, ...groupingFields];

  const getFinalSelectedFields = () => {
    return groupedSelectedFields.map(entity => entity.options.map(opt => opt.value)).flat()
  }


  const validateColumnSelection = () => {
    const fields = getFinalSelectedFields()

    /* Check that the user didn't select too many columns */
    if (fields.length > FIELDS_LIMIT) {
      setErrors('Only ' + FIELDS_LIMIT + ' columns can be selected, but ' + fields.length +
        ' are currently selected. Please remove some columns and try again.');
      return false;
    }

    /* Check that the user selected required grouping options */
    if (!rowGroupingFields && !!displayType) {
      setErrors('Some required fields have not been completed. Please complete and try again.');
      return false;
    }

    setErrors(undefined);
    return true;
  }

  const onSubmit = () => {
    setSubmitting(true)
    if (!validateColumnSelection()){
      setSubmitting(false);
      return;
    }

    // update fields on haml form and submit
    SearchHelper.prepareSearchForm();
    $('#search_type').val(displayType);
    $('#selected_field_ids').val(getFinalSelectedFields());
    $('#search_fields_are_default').val('false');
    $('#search_row_grouping_fields').val(rowGroupingFields);
    $('#search_column_grouping_fields').val(columnGroupingFields);
    $('#search_form').submit();
  };

  const editingColumns = displayType === 'GroupingReport' || !displayType

  return (
    <ModalComponent
      className='wider-modal'
      bodyClassName={classnames(editingColumns ? 'special-scrolling-behavior' : '')}
      title='Results Display'
      buttons={[<Button className='btn btn-primary' disabled={submitting} onClick={onSubmit}>{submitting ? 'Loading...' : 'Apply'}</Button>]}>
      <div className={classnames('change-columns-modal', 'with-graph')} id='change-columns-modal'>
        {errors && <div className='alert alert-danger validation-error column-select-errors'>{errors}</div>}

        <DisplayTypeSelector displayType={displayType} setType={setType} />

        {editingColumns && (<SearchWithColumns
          displayType={displayType} groupingOptions={groupingOptions} overallFields={overallFields}
          rowGroupingFields={rowGroupingFields} setRowGroupingFields={setRowGroupingFields}
          groupedSelectedFields={groupedSelectedFields} setGroupedSelectedFields={setGroupedSelectedFields}
        />)}
        {displayType === 'SummaryReport' && (<SummaryReportSearch
          columnGroupingFields={columnGroupingFields} setColumnGroupingFields={setColumnGroupingFields}
          rowGroupingFields={rowGroupingFields} setRowGroupingFields={setRowGroupingFields} groupingOptions={groupingOptions}
        />)}
        {displayType === 'GraphingReport' && (<GraphReportSearch
          rowGroupingFields={rowGroupingFields} setRowGroupingFields={setRowGroupingFields} groupingOptions={groupingOptions}
        />)}
      </div>
    </ModalComponent>)
}

const ChangeColumnsEditor = ({searchConfigsFields,selectedFields, searchType, rowGroupingField, columnGroupingField, groupingFields, duckMeta}) => {

  if (duckMeta.searchConfigsFields.ready && duckMeta.groupingFields.ready && duckMeta.selectedFields.ready) {

    return <LoadedColumnsEditor columnGroupingField={columnGroupingField} selectedFields={selectedFields} searchConfigsFields={searchConfigsFields} searchType={searchType} groupingFields={groupingFields} rowGroupingField={rowGroupingField} />
  }

  return (<ModalComponent
    className='wider-modal'
    title='Results Display'
  >
    <Placeholder.loading />
  </ModalComponent>);
}

const mapDucksToProps = ({searchId}) => {
  return {
    groupingFields: filterDuck(searchConfigsGroupingFields, {}, 'groupingFieldsForUser'),
    searchConfigsFields: filterDuck(searchConfigsFields, {}, 'fieldsForUser'),
    selectedFields: filterDuck(selectedFields, {search_id: searchId}, `selectedFieldsFor${searchId}`)
  };
};


export default compose(
  duckify(mapDucksToProps)
)(ChangeColumnsEditor)
